import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import styles from "../style/Header.module.css";
import AWNLogo from "./AWNLogo";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import supabase from "../supabaseClient";
import { useTutorial } from "../context/TutorialContext";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

function HeaderAvatar({ path }) {
  const [avatarUrl, setAvatarUrl] = useState(null);

  useEffect(() => {
    if (path) {
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      if (data && data.publicUrl) {
        setAvatarUrl(`${data.publicUrl}?t=${new Date().getTime()}`);
      }
    } else {
      setAvatarUrl(null);
    }
  }, [path]);

  if (!avatarUrl) return null;

  return <img src={avatarUrl} alt="Avatar" className={styles.headerAvatar} />;
}

function Header() {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [openCacheDialog, setOpenCacheDialog] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);

  const apiBase = (process.env.REACT_APP_API_URL || "/api").replace(/\/+$/, "");

  const { user, logout, userProducts, profile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { startTutorial, joyrideState, goToNextStep } = useTutorial();

  const hasAnomaly = userProducts?.some(
    (p) => p?.product_name === "anomaly" && p?.is_active
  );

  const planLabel = userProducts?.some((p) => p?.is_active) ? "Pro" : "Free";
  const avatarPath = profile?.avatar_url;

  const fullNameRaw =
    profile?.full_name || user?.user_metadata?.full_name || user?.email || "";
  const fullName = useMemo(() => String(fullNameRaw || ""), [fullNameRaw]);

  const initials = useMemo(() => {
    if (!fullName) return "";
    if (fullName.includes("@")) return fullName[0]?.toUpperCase() || "";
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    const two = parts.map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    return two || fullName.slice(0, 2).toUpperCase();
  }, [fullName]);

  const compactIdentity = useMemo(() => {
    if (!fullName) return "Konto";
    if (fullName.includes("@")) {
      return fullName.split("@")[0]?.slice(0, 18) || "Konto";
    }
    return fullName;
  }, [fullName]);

  const displayName = fullName.includes("@") ? "Nutzerkonto" : fullName;

  useEffect(() => {
    setIsNavOpen(false);
    setProfileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsNavOpen(false);
        setProfileMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
    document.body.style.overflow = "";
  }, [isNavOpen]);

  useEffect(() => {
    if (!isNavOpen) setProfileMenuOpen(false);
  }, [isNavOpen]);

  const handleUserGuideClick = () => {
    localStorage.removeItem("tutorial_header_completed");
    localStorage.removeItem("tutorial_dashboard_completed");
    localStorage.removeItem("tutorial_anomaly_completed");

    if (location.pathname.includes("/dashboard")) {
      startTutorial("dashboard");
    } else if (location.pathname.includes("/anomaly-detection")) {
      startTutorial("anomaly");
    } else {
      startTutorial("header");
    }
    setProfileMenuOpen(false);
  };

  const handleProfileMenuToggle = () => {
    setProfileMenuOpen((open) => !open);

    const currentStep = joyrideState.steps?.[joyrideState.stepIndex];
    if (
      joyrideState.run &&
      currentStep &&
      currentStep.target === '[data-tut="profile-user-box"]'
    ) {
      goToNextStep();
    }
  };

  const handleDropdownMouseLeave = () => {
    if (joyrideState.run) return;
    setProfileMenuOpen(false);
  };

  const handleClearCacheClick = () => {
    setProfileMenuOpen(false);
    setOpenCacheDialog(true);
  };

  const handleExecuteClearCache = async () => {
    setIsClearingCache(true);
    try {
      const response = await fetch(`${apiBase}/clear-cache`, {
        method: "POST",
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Cache konnte nicht geleert werden.");
      }
    } catch (err) {
      console.error("Error clearing cache:", err);
      alert(`Fehler: ${err.message}`);
    } finally {
      setIsClearingCache(false);
      setOpenCacheDialog(false);
    }
  };

  const closeDrawer = () => setIsNavOpen(false);

  const dialogPaperStyle = {
    borderRadius: "20px",
    padding: "10px",
    backgroundColor: "var(--modal-bg)",
    color: "var(--text-color)",
    border: "1px solid var(--border-color)",
    boxShadow: "var(--elevated-shadow)",
  };

  const titleColor = "var(--accent-color)";
  const textColor = "var(--text-soft)";
  const cancelBtnColor = "var(--text-muted)";

  return (
    <>
      <header className={styles.mainHeader}>
        <div className={styles.logo} data-tut="header-logo">
          <Link to="/">
            <AWNLogo width={200} height={60} className={styles.logoSvg} />
          </Link>
        </div>

        <button
          className={styles.mobileNavToggle}
          onClick={() => setIsNavOpen(true)}
          aria-label="Open menu"
          aria-expanded={isNavOpen ? "true" : "false"}
          type="button"
        >
          ☰
        </button>

        <nav className={styles.mainNav} data-tut="header-nav-links">
          <ul className={styles.navLinks}>
            <li>
              <Link to="/chatbot" className={styles.navBtn}>
                Chatbot
              </Link>
            </li>

            {hasAnomaly && (
              <li>
                <Link to="/dashboard" className={styles.navBtn}>
                  Dashboard
                </Link>
              </li>
            )}

            <li>
              <Link to="/anomaly-detection" className={styles.navBtn}>
                Anomalie-Erkennung
              </Link>
            </li>
          </ul>
        </nav>

        <div className={styles.headerActions}>
          <a
            href="https://awn-projekte.de/kontakt/"
            className={styles.contactBtn}
            target="_blank"
            rel="noopener noreferrer"
            data-tut="header-contact-btn"
          >
            Kontakt
          </a>

          {user ? (
            <div className={styles.desktopProfileWrap}>
              <button
                type="button"
                className={styles.userBox}
                title={fullName}
                onClick={handleProfileMenuToggle}
                data-tut="profile-user-box"
              >
                <span className={styles.userAvatarShell}>
                  {avatarPath ? <HeaderAvatar path={avatarPath} /> : initials}
                </span>
                <span className={styles.userMeta}>
                  <span className={styles.userNameInline}>{compactIdentity}</span>
                  <span className={styles.userPlanInline}>{planLabel} Plan</span>
                </span>
                <span
                  className={`${styles.userChevron} ${
                    profileMenuOpen ? styles.userChevronOpen : ""
                  }`}
                  aria-hidden="true"
                >
                  ▾
                </span>
              </button>

              {profileMenuOpen && (
                <div
                  className={styles.profileDropdown}
                  onMouseLeave={handleDropdownMouseLeave}
                >
                  <div className={styles.profileInfoBox}>
                    <div className={styles.profileBadgeRow}>
                      <span className={styles.profilePlanBadge}>{planLabel} Plan</span>
                      <span className={styles.profileStatus}>Aktiv</span>
                    </div>
                    <div className={styles.profileName}>{displayName}</div>
                    <div className={styles.profileEmail}>{user?.email}</div>
                  </div>

                  <div
                    className={styles.themeToggle}
                    data-tut="profile-theme-toggle"
                  >
                    <div className={styles.toggleLabelWrap}>
                      <span className={styles.dropdownLabel}>Darstellung</span>
                      <strong>{theme === "light" ? "Heller Modus" : "Dunkler Modus"}</strong>
                    </div>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        onChange={toggleTheme}
                        checked={theme === "dark"}
                      />
                      <span className={styles.slider}></span>
                    </label>
                  </div>

                  <div className={styles.dropdownSectionLabel}>Konto</div>

                  <button
                    type="button"
                    onClick={() => {
                      navigate("/profile");
                      setProfileMenuOpen(false);
                    }}
                    className={styles.dropdownBtn}
                    data-tut="profile-edit"
                  >
                    Profil bearbeiten
                  </button>

                  <button
                    type="button"
                    onClick={handleUserGuideClick}
                    className={styles.dropdownBtn}
                    data-tut="profile-user-guide"
                  >
                    Benutzerhandbuch
                  </button>

                  <button
                    type="button"
                    onClick={handleClearCacheClick}
                    className={styles.dropdownBtn}
                    title="Löscht die Liste der verarbeiteten Dateien im Server-Speicher"
                    data-tut="profile-clear-cache"
                  >
                    Server-Cache leeren
                  </button>

                  <button
                    type="button"
                    onClick={logout}
                    className={`${styles.dropdownBtn} ${styles.hotspotBtn}`}
                    data-tut="profile-logout"
                  >
                    Abmelden
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className={styles.loginBtn}>
              Anmelden
            </Link>
          )}
        </div>
      </header>

      <div
        className={`${styles.drawerOverlay} ${
          isNavOpen ? styles.drawerOverlayOpen : ""
        }`}
        onClick={closeDrawer}
        aria-hidden={!isNavOpen}
      />

      <aside
        className={`${styles.drawer} ${isNavOpen ? styles.drawerOpen : ""}`}
        aria-hidden={!isNavOpen}
      >
        <div className={styles.drawerHeader}>
          <div className={styles.drawerTitle}>Menü</div>
          <button
            className={styles.drawerCloseBtn}
            onClick={closeDrawer}
            aria-label="Close menu"
            type="button"
          >
            ✕
          </button>
        </div>

        <nav className={styles.drawerNav}>
          <Link to="/chatbot" className={styles.drawerLink} onClick={closeDrawer}>
            Chatbot
          </Link>

          {hasAnomaly && (
            <Link
              to="/dashboard"
              className={styles.drawerLink}
              onClick={closeDrawer}
            >
              Dashboard
            </Link>
          )}

          <Link
            to="/anomaly-detection"
            className={styles.drawerLink}
            onClick={closeDrawer}
          >
            Anomalie-Erkennung
          </Link>

          <a
            href="https://awn-projekte.de/kontakt/"
            className={styles.drawerLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeDrawer}
          >
            Kontakt
          </a>

          {!user && (
            <Link to="/login" className={styles.drawerLink} onClick={closeDrawer}>
              Anmelden
            </Link>
          )}
        </nav>
      </aside>

      {user && isNavOpen && (
        <>
          <div className={styles.floatingAccountPill} title={user?.email}>
            <div className={styles.floatingAccountEmail}>{user?.email}</div>
            <div className={styles.floatingAccountPlan}>{planLabel}</div>
          </div>

          <button
            className={styles.floatingProfileBtn}
            onClick={handleProfileMenuToggle}
            aria-label="Open profile menu"
            type="button"
          >
            <span className={styles.floatingProfileInner} title={fullName}>
              {avatarPath ? <HeaderAvatar path={avatarPath} /> : initials}
            </span>
          </button>
        </>
      )}

      {user && profileMenuOpen && (
        <div className={styles.mobileProfileDropdownWrap}>
          <div
            className={styles.mobileProfileBackdrop}
            onClick={() => setProfileMenuOpen(false)}
          />
          <div className={styles.mobileProfileDropdown}>
            <div className={styles.profileInfoBox}>
              <div className={styles.profileBadgeRow}>
                <span className={styles.profilePlanBadge}>{planLabel} Plan</span>
                <span className={styles.profileStatus}>Aktiv</span>
              </div>
              <div className={styles.profileName}>{displayName}</div>
              <div className={styles.profileEmail}>{user?.email}</div>
            </div>

            <div className={styles.themeToggle}>
              <div className={styles.toggleLabelWrap}>
                <span className={styles.dropdownLabel}>Darstellung</span>
                <strong>{theme === "light" ? "Heller Modus" : "Dunkler Modus"}</strong>
              </div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  onChange={toggleTheme}
                  checked={theme === "dark"}
                />
                <span className={styles.slider}></span>
              </label>
            </div>

            <button
              type="button"
              className={styles.dropdownBtn}
              onClick={() => {
                navigate("/profile");
                setProfileMenuOpen(false);
                closeDrawer();
              }}
            >
              Profil bearbeiten
            </button>

            <button
              type="button"
              className={styles.dropdownBtn}
              onClick={handleUserGuideClick}
            >
              Benutzerhandbuch
            </button>

            <button
              type="button"
              className={styles.dropdownBtn}
              onClick={handleClearCacheClick}
            >
              Server-Cache leeren
            </button>

            <button
              type="button"
              className={`${styles.dropdownBtn} ${styles.hotspotBtn}`}
              onClick={logout}
            >
              Abmelden
            </button>
          </div>
        </div>
      )}

      <Dialog
        open={openCacheDialog}
        onClose={() => !isClearingCache && setOpenCacheDialog(false)}
        PaperProps={{ style: dialogPaperStyle }}
      >
        <DialogTitle style={{ color: titleColor, fontWeight: 800 }}>
          Server-Cache leeren?
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: textColor }}>
            Dadurch werden die zwischengespeicherten verarbeiteten Dateien im
            Server-Speicher gelöscht. Beim nächsten Analyselauf werden die Daten
            neu verarbeitet.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "0 24px 16px" }}>
          <Button
            onClick={() => setOpenCacheDialog(false)}
            disabled={isClearingCache}
            sx={{ color: cancelBtnColor, fontWeight: 700 }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleExecuteClearCache}
            variant="contained"
            disabled={isClearingCache}
            sx={{
              background: "var(--accent-gradient)",
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 800,
              paddingInline: "18px",
              boxShadow: "var(--card-shadow)",
            }}
          >
            {isClearingCache ? "Wird geleert..." : "Bestätigen"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Header;
